import { FC, createRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Alert, Button, FormControl, FormHelperText, Grid, Typography } from '@mui/material';
import { TextField } from '@/components/forms/inputs/TextField/TextField';
import { CenteredContentWrapper } from '@/components/layout/CenteredContentWrapper/CenteredContentWrapper';
import { useAuthStore, useConfigStore } from '@/store';
import { useSearchParams } from 'next/navigation';
import { SubmitHandler, useForm } from 'react-hook-form';
import { PASSWORD_RESET_PHRASES } from '@/consts/phrases/loginPhrases';
import { useRouter, usePhrase, useHasHydrated, useServerErrors } from '@/hooks';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';

import styles from '@/styles/modules/Auth.module.css';

interface ForgottenPasswordEmailFormValues {
    email: string;
    captchaResponse: string | undefined;
}

export const ForgottenPasswordEmailForm: FC = () => {
    const [resetPasswordSent, setResetPasswordSent] = useState(false);
    const requestPasswordReset = useAuthStore((state) => state.requestPasswordReset);
    const reCaptchaRef = createRef<ReCAPTCHA>();
    const recaptchaKey = useConfigStore((state) => state.website_google_recaptcha_v2_site_key);

    const router = useRouter();
    const { t } = usePhrase();
    const hasHydrated = useHasHydrated();
    const searchParams = useSearchParams();
    const initialEmail = searchParams.get('email') || '';

    const { control, handleSubmit, formState, setValue, getValues, setError, clearErrors } =
        useForm<ForgottenPasswordEmailFormValues>({
            defaultValues: {
                email: initialEmail,
                captchaResponse: undefined,
            },
        });

    const { handleServerErrors, serverErrors, clearAllErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const { errors, isSubmitting } = formState;

    const onSubmit: SubmitHandler<ForgottenPasswordEmailFormValues> = async (data) => {
        const res = await requestPasswordReset(data.email, data.captchaResponse ?? '');
        clearAllErrors();
        if (res.type === 'success') {
            setResetPasswordSent(true);
        } else {
            setResetPasswordSent(false);
            handleServerErrors(res.error);
        }
    };

    const goToLoginPage = () => {
        router.push('/login');
    };

    const recaptchaError = errors.captchaResponse?.message;

    const handleCaptchaChange = (value: string | null) => {
        if (value) clearErrors('captchaResponse');
        setValue('captchaResponse', value ?? '');
    };

    return (
        <CenteredContentWrapper>
            <form
                aria-label="forgotten_password_form"
                onSubmit={handleSubmit(onSubmit)}
                className={styles.auth}
                noValidate
            >
                <Grid container spacing={1}>
                    {!resetPasswordSent && (
                        <Grid size={12}>
                            <Typography variant="h2" align="center" className={styles.title}>
                                {t(PASSWORD_RESET_PHRASES.ENTER_YOUR_EMAIL)}
                            </Typography>
                            <Typography variant="subtitle1">
                                {t(PASSWORD_RESET_PHRASES.WE_WILL_EMAIL_PASSWORD_RESET)}
                            </Typography>
                        </Grid>
                    )}

                    {resetPasswordSent && (
                        <Grid size={12} className={styles.errorBlock}>
                            <Alert severity="success" id="message_box">
                                {t(PASSWORD_RESET_PHRASES.PASSWORD_UPDATE_EMAIL_SENT)}
                            </Alert>
                        </Grid>
                    )}

                    {hasHydrated && (
                        <FormGridErrors
                            errors={serverErrors}
                            gridSx={{ color: 'red', textAlign: 'center', my: 2 }}
                            generalErrorId="message_box"
                        />
                    )}

                    {!resetPasswordSent && (
                        <>
                            <Grid size={12} sx={{ mt: 2 }}>
                                <FormControl fullWidth>
                                    <TextField
                                        name="email"
                                        control={control}
                                        id="email_field"
                                        type="text"
                                        required
                                        fullWidth
                                        label={PASSWORD_RESET_PHRASES.EMAIL}
                                        error={errors.email}
                                        slotProps={{
                                            htmlInput: {
                                                autoComplete: 'off',
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid size={12}>
                                <ReCAPTCHA
                                    data-testid="recaptcha"
                                    ref={reCaptchaRef}
                                    sitekey={recaptchaKey}
                                    className="google-recaptcha"
                                    onChange={handleCaptchaChange}
                                />
                                {recaptchaError && (
                                    <FormHelperText error>{recaptchaError}</FormHelperText>
                                )}
                            </Grid>
                            <Grid size={12} sx={{ mt: 2 }}>
                                <Button
                                    fullWidth
                                    id="send-reset-link"
                                    type="submit"
                                    variant="contained"
                                    loading={isSubmitting}
                                >
                                    {t(PASSWORD_RESET_PHRASES.SEND)}
                                </Button>
                            </Grid>
                        </>
                    )}
                </Grid>
            </form>
            <Grid size={12} sx={{ mt: 2 }}>
                <Button
                    onClick={goToLoginPage}
                    fullWidth
                    id="back-to-login"
                    type="submit"
                    variant="outlined"
                >
                    {t(PASSWORD_RESET_PHRASES.BACK_TO_LOGIN_PAGE)}
                </Button>
            </Grid>
        </CenteredContentWrapper>
    );
};
